'use strict'

import { useState, useEffect } from 'react'

function getBreakPoint(windowWidth) {
  // this function uses following values for breakpoints:
  // xs: 0,
  // sm: 640px,
  // md: 960px,
  // lg: 1280px,
  // xl: 1600px,
  // xxl: 1920px

  if (windowWidth) {
    let breakpoint = 'xxl'

    switch (true) {
      case windowWidth < 640:
        breakpoint = 'xs'
        break
      case windowWidth < 960:
        breakpoint = 'sm'
        break
      case windowWidth < 1280:
        breakpoint = 'md'
        break
      case windowWidth < 1600:
        breakpoint = 'lg'
        break
      case windowWidth < 1920:
        breakpoint = 'xl'
        break
      default:
        breakpoint = 'xxl'
    }
    return breakpoint
  }

  return undefined
}

// Shared viewport-width cache. Reading window.innerWidth forces a synchronous
// reflow, so N hook instances reading it independently at mount caused N
// forced reflows (top contributor in item page traces). All instances share
// one cached read and a single resize listener.
let cachedWidth = null
const subscribers = new Set()
let resizeHandler = null

function getCachedWidth() {
  if (cachedWidth === null) {
    cachedWidth = window.innerWidth
  }

  return cachedWidth
}

function subscribe(callback) {
  subscribers.add(callback)

  if (!resizeHandler) {
    resizeHandler = () => {
      cachedWidth = window.innerWidth

      subscribers.forEach((subscriber) => subscriber(cachedWidth))
    }

    window.addEventListener('resize', resizeHandler)
  }

  return () => subscribers.delete(callback)
}

// Test-only helper: module-level state would otherwise leak between specs
function resetBreakpointCache() {
  cachedWidth = null
  subscribers.clear()

  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
    resizeHandler = null
  }
}

function useBreakpoint() {
  const isClient = typeof window !== 'undefined'
  const [windowSize, setWindowSize] = useState(
    isClient ? getBreakPoint(getCachedWidth()) : undefined
  )

  useEffect(() => {
    setWindowSize(getBreakPoint(getCachedWidth()))

    return subscribe((width) => setWindowSize(getBreakPoint(width)))
  }, [])

  return windowSize
}

export { getBreakPoint, resetBreakpointCache }

export default useBreakpoint
